// ─────────────────────────────────────────────────────────────────────────────
// DemoBadge.jsx — the only demo-mode chrome that appears over the real pages.
//
// Three jobs:
//   1. A small DEMO pill, so nobody mistakes fixtures for live data.
//   2. The toast host. Read-only saves report here instead of through the
//      pages' own alert() calls, which would put a browser modal on screen
//      mid-presentation.
//   3. The edit-drawer trigger — hidden by default, because your boss should
//      never see an "Edit demo data" button while he is being shown the product.
//
// PRESENTATION SWITCHES
//   Ctrl+Shift+E   toggle the edit button
//   Ctrl+Shift+D   toggle the DEMO pill
// Both persist, so you set them once before the meeting and they stay set.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, FlaskConical, X } from 'lucide-react';
import { getSettings, setSetting, subscribe } from './demoStore';
import { isDemoMode, TOAST_EVENT, DEMO_CHANGE_EVENT } from './installDemoAdapter';
import DemoEditorDrawer from './DemoEditorDrawer';

const TOAST_MS = 3200;

const TONE = {
  info: { bg: 'rgba(96,165,250,0.16)', border: 'rgba(96,165,250,0.4)', fg: '#60A5FA' },
  warn: { bg: 'rgba(245,158,11,0.16)', border: 'rgba(245,158,11,0.4)', fg: '#F59E0B' },
  good: { bg: 'rgba(34,211,165,0.16)', border: 'rgba(34,211,165,0.4)', fg: '#22D3A5' },
};

let toastSeq = 0;

const DemoBadge = () => {
  const [active, setActive] = useState(() => isDemoMode());
  const [settings, setSettings] = useState(() => getSettings());
  const [toasts, setToasts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Store changes (drawer edits, setting flips) and login/logout both need to
  // re-read state — demo mode turns on the moment the demo account signs in.
  useEffect(() => subscribe(() => setSettings(getSettings())), []);
  useEffect(() => {
    const sync = () => { setActive(isDemoMode()); setSettings(getSettings()); };
    window.addEventListener(DEMO_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(DEMO_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const pushToast = useCallback((detail) => {
    toastSeq += 1;
    const id = toastSeq;
    setToasts((prev) => [...prev, { id, ...detail }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), TOAST_MS);
  }, []);

  useEffect(() => {
    const onToast = (e) => pushToast(e.detail || { message: '', tone: 'info' });
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, [pushToast]);

  useEffect(() => {
    const onKey = (e) => {
      if (!e.ctrlKey || !e.shiftKey) return;
      const k = e.key.toLowerCase();
      if (k === 'e') {
        e.preventDefault();
        const next = !getSettings().editor;
        setSetting('editor', next);
        pushToast({ message: next ? 'Edit tools on' : 'Edit tools hidden', tone: 'info' });
      } else if (k === 'd') {
        e.preventDefault();
        const next = getSettings().badge === false;
        setSetting('badge', next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pushToast]);

  if (!active) return null;

  const showBadge = settings.badge !== false;
  const showEditor = Boolean(settings.editor);

  return (
    <>
      <div
        style={{
          position: 'fixed', right: 18, bottom: 18, zIndex: 4000,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10,
          pointerEvents: 'none', fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
      >
        {toasts.map((t) => {
          const tone = TONE[t.tone] || TONE.info;
          return (
            <div
              key={t.id}
              style={{
                pointerEvents: 'auto',
                background: 'var(--nd-surface, #14171C)',
                border: `1px solid ${tone.border}`,
                borderLeft: `3px solid ${tone.fg}`,
                borderRadius: 10, padding: '10px 14px', maxWidth: 320,
                fontSize: 12.5, fontWeight: 600, color: 'var(--nd-text, #E6E8EC)',
                boxShadow: '0 8px 28px rgba(0,0,0,0.38)',
              }}
            >
              {t.message}
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', pointerEvents: 'auto' }}>
          {showEditor && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              title="Edit demo data (Ctrl+Shift+E to hide this button)"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.38)',
                color: '#60A5FA', borderRadius: 999, padding: '8px 14px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'DM Sans', system-ui, sans-serif",
              }}
            >
              <Pencil size={13} /> Edit demo data
            </button>
          )}

          {showBadge && (
            <div
              title="Demo mode — data on these pages is local, not live. Ctrl+Shift+D to hide."
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.38)',
                color: '#F59E0B', borderRadius: 999, padding: '7px 13px',
                fontSize: 10.5, fontWeight: 800, letterSpacing: '0.14em',
              }}
            >
              <FlaskConical size={12} /> DEMO
              <button
                type="button"
                onClick={() => setSetting('badge', false)}
                aria-label="Hide demo badge"
                style={{
                  background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
                  padding: 0, marginLeft: 2, display: 'flex', opacity: 0.7,
                }}
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && <DemoEditorDrawer onClose={() => setDrawerOpen(false)} />}
    </>
  );
};

export default DemoBadge;
